import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
export const ADMIN_ACTIVE_JOURNAL_KEY = 'uzjs_admin_active_journal';

export const getActiveJournalSlug = () => {
  if (typeof window === 'undefined') return '';
  return (localStorage.getItem(ADMIN_ACTIVE_JOURNAL_KEY) || '').trim();
};

export const setActiveJournalSlug = (slug) => {
  if (typeof window === 'undefined') return;
  const normalized = (slug || '').trim();
  if (normalized) {
    localStorage.setItem(ADMIN_ACTIVE_JOURNAL_KEY, normalized);
    return;
  }
  localStorage.removeItem(ADMIN_ACTIVE_JOURNAL_KEY);
};

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add JWT token to requests
const TOKEN_KEY = 'uzjs_token';
const getToken = () => localStorage.getItem(TOKEN_KEY) || localStorage.getItem('token');

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const journalSlug = getActiveJournalSlug();
  const requestUrl = config.url || '';
  if (journalSlug && !requestUrl.startsWith('/auth/login')) {
    config.params = {
      ...(config.params || {}),
      journal_slug: config.params?.journal_slug || journalSlug,
    };
  }
  return config;
});

// Handle 401 — dispatch custom event so React can handle auth state cleanly
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.hash.includes('/login')) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('token');
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    // Retry logic for network errors or 5xx (not 4xx)
    const config = error.config;
    if (!config || config.__retryCount >= 2) {
      return Promise.reject(error);
    }
    if (!error.response || error.response.status >= 500) {
      config.__retryCount = (config.__retryCount || 0) + 1;
      return new Promise(resolve => setTimeout(resolve, 1000 * config.__retryCount))
        .then(() => api(config));
    }

    return Promise.reject(error);
  }
);

// ── Auth ──
export const login = (email, password) =>
  api.post('/auth/login', { email, password });
export const getCurrentAdminProfile = () => api.get('/auth/me');
export const getJournals = (params) => api.get('/journals/', { params });

// ── Stats ──
export const getStats = () => api.get('/stats');
export const getAIUsage = () => api.get('/ai-usage');

// ── Issues ──
export const getIssues = () => api.get('/issues/');
export const createIssue = (data) => api.post('/issues/', data);
export const updateIssue = (id, data) => api.patch(`/issues/${id}`, data);
export const toggleIssue = (id) => api.patch(`/issues/${id}/toggle`);

// ── Submissions ──
export const getSubmissions = (params) => api.get('/submissions/', { params });
export const getSubmission = (id) => api.get(`/submissions/${id}`);
export const adminCreateSubmission = (data) =>
  api.post('/submissions/admin-create', data);
export const deleteSubmission = (id) => api.delete(`/submissions/${id}`);
export const updateSubmissionStatus = (id, data) =>
  api.patch(`/submissions/${id}/status`, data);
export const bulkStatusUpdate = (data) =>
  api.patch('/submissions/bulk-status', data);
export const sendToReviewer = (id, data) =>
  api.post(`/submissions/${id}/send-to-reviewer`, data);
export const updateReview = (submissionId, reviewId, data) =>
  api.patch(`/submissions/${submissionId}/review/${reviewId}`, data);
export const publishSubmission = (id, force = false) =>
  api.post(`/submissions/${id}/publish${force ? '?force=true' : ''}`);
export const getSections = () => api.get('/submissions/sections');
export const getSubmissionAuditLog = (id, params = {}) =>
  api.get(`/submissions/${id}/audit-log`, { params });
export const searchSubmissions = (params) =>
  api.get('/submissions/', { params });

// DOI status dashboard: list submissions with their DOI assignment status.
// Filter values for `doi_status`: 'all' | 'assigned' | 'pending' | 'missing' | 'failed'.
export const getDoiDashboard = (params = {}) =>
  api.get('/submissions/doi-dashboard', { params });

// Download certificate ZIP for a submission. ``kind`` is "published" (default)
// or "accepted". Returns the underlying axios response with binary blob so
// callers can save it via the browser. Triggers an attachment download.
export const downloadCertificates = (id, kind = 'published') =>
  api.get(`/submissions/${id}/certificates`, {
    params: { kind },
    responseType: 'blob',
  });
export const sendManualReminder = (id) =>
  api.post(`/submissions/${id}/remind`);
export const extendRevisionDeadline = (id, extraDays) =>
  api.post(`/submissions/${id}/extend-deadline?extra_days=${extraDays}`);
export const getReminderStats = () => api.get('/submissions/reminder-stats');

// ── DOI ──
export const assignDoi = (id) =>
  api.post(`/submissions/${id}/assign-doi`);
export const updateZenodoPdf = (id) =>
  api.post(`/submissions/${id}/update-zenodo-pdf`);
export const batchAssignDoi = (submissionIds) =>
  api.post('/submissions/batch-assign-doi', { submission_ids: submissionIds });

// ── Submission Metadata (page numbers etc.) ──
export const updateSubmissionMeta = (id, data) =>
  api.patch(`/submissions/${id}/meta`, data);

// ── Authors ──
export const getAuthors = () => api.get('/authors/');
export const lookupOrcid = (orcid) =>
  api.get(`/authors/lookup-orcid/${encodeURIComponent(orcid)}`);
export const getReviewers = () => api.get('/authors/reviewers');
export const getAuthorPublishedArticles = (authorId) =>
  api.get(`/authors/${authorId}/published-articles`);
export const toggleReviewer = (id) => api.patch(`/authors/${id}/toggle-reviewer`);
export const updateAuthor = (id, data) => api.put(`/authors/${id}`, data);
export const deleteAuthor = (id) => api.delete(`/authors/${id}`);

// ── AI Review ──
export const runAIReview = (id) => api.post(`/submissions/${id}/ai-review`);

// ── Mark as Read ──
export const markAsRead = (id) => api.patch(`/submissions/${id}/mark-read`);

// ── Files ──
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getFileUrl = (filename) => `${API_BASE}/files/${filename}`;
export const getFilePreviewUrl = (filename) => `${API_BASE}/files/${filename}?preview=true`;
export const fetchFileBlob = (filename, preview = false) =>
  api.get(`/files/${filename}`, {
    params: preview ? { preview: true } : {},
    responseType: 'blob',
  });

// ── Payment ──
export const confirmPayment = (id) =>
  api.patch(`/submissions/${id}/confirm-payment`);
export const rejectPayment = (id, comment) =>
  api.patch(`/submissions/${id}/reject-payment`, null, { params: { comment } });

// ── Build PDF from ordered submissions ──
export const buildSectionPdf = (data) =>
  api.post('/submissions/build-pdf', data, { responseType: 'blob' });

// ── Prepare PDFs (combined + individual article PDFs) ──
export const preparePdfs = (data) =>
  api.post('/submissions/prepare-pdfs', data);

// ── Get article PDF blob (inline preview via object URL) ──
export const getArticlePdfBlob = (submissionId) =>
  api.get(`/submissions/${submissionId}/article-pdf`, { responseType: 'blob' });

// ── Download per-author certificates ZIP for a submission ──
export const getCertificatesZip = (submissionId, kind = 'published') =>
  api.get(`/submissions/${submissionId}/certificates`, {
    params: { kind },
    responseType: 'blob',
  });

// ── Get combined PDF URL (via files endpoint with preview) ──
export const getCombinedPdfUrl = (filename) =>
  `${API_BASE}/files/${filename}?preview=true`;

// ── Publish to site ──
export const publishToSite = (data) =>
  api.post('/submissions/publish-to-site', data);

// ── LaTeX API ──
export const latexHealth = () => api.get('/latex/health');
export const latexPreflight = () => api.get('/latex/preflight');
export const latexListIssues = (year) =>
  api.get('/latex/issues', { params: year ? { year } : {} });
export const latexGetIssue = (year, issue) =>
  api.get(`/latex/issues/${year}/${issue}`);
export const latexGetArticles = (year, issue) =>
  api.get(`/latex/issues/${year}/${issue}/articles`);
export const latexCreateIssue = (year, issue) =>
  api.post(`/latex/issues/${year}/${issue}/create`);
export const latexUploadArticle = (formData) =>
  api.post('/latex/articles/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const latexDeleteArticle = (year, issue, article) =>
  api.delete(`/latex/articles/${year}/${issue}/${article}`);
export const latexBuildIssue = (year, issue) =>
  api.post(`/latex/issues/${year}/${issue}/build`);
export const latexBuildStatus = (jobId) =>
  api.get(`/latex/builds/${jobId}`);
export const latexDownloadPdf = (year, issue) =>
  api.get(`/latex/issues/${year}/${issue}/pdf`, { responseType: 'blob' });
export const latexReorderArticles = (year, issue, order) =>
  api.post(`/latex/articles/${year}/${issue}/reorder`, { order });
export const latexPreviewPdf = (year, issue) =>
  api.get(`/latex/issues/${year}/${issue}/pdf-preview`, { responseType: 'blob' });

// ── LaTeX Sections ──
export const latexListSections = (year, issue) =>
  api.get(`/latex/issues/${year}/${issue}/sections`);
export const latexBuildSection = (year, issue, section) =>
  api.post(`/latex/issues/${year}/${issue}/build-section`, null, {
    params: { section },
  });
export const latexDownloadSectionPdf = (year, issue, section) =>
  api.get(`/latex/issues/${year}/${issue}/pdf-section`, {
    params: { section },
    responseType: 'blob',
  });
export const latexPreviewSectionPdf = (year, issue, section) =>
  api.get(`/latex/issues/${year}/${issue}/pdf-section-preview`, {
    params: { section },
    responseType: 'blob',
  });

// ── Export CSV ──
export const exportSubmissionsCsv = (issueId) => {
  const query = new URLSearchParams();
  if (issueId) query.set('issue_id', issueId);
  const journalSlug = getActiveJournalSlug();
  if (journalSlug) query.set('journal_slug', journalSlug);
  const params = query.toString() ? `?${query.toString()}` : '';
  const token = getToken();
  // Direct download — bypass axios for blob
  const url = `${API_BASE}/submissions/export${params}`;
  return fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      if (!res.ok) throw new Error('Export failed');
      return res.blob();
    })
    .then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'submissions_export.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    });
};

// ── Promotions ──
export const getPromotions = () => api.get('/promotions/');
export const createPromotion = (data) => api.post('/promotions/', data);
export const updatePromotion = (id, data) => api.patch(`/promotions/${id}`, data);
export const deletePromotion = (id) => api.delete(`/promotions/${id}`);

// ── Editorial Board ──
export const getEditorialBoardMembers = () => api.get('/editorial-board/');
export const createEditorialBoardMember = (data) => api.post('/editorial-board/', data);
export const updateEditorialBoardMember = (id, data) => api.patch(`/editorial-board/${id}`, data);
export const deleteEditorialBoardMember = (id) => api.delete(`/editorial-board/${id}`);
export const reorderEditorialBoardMembers = (orderedIds) =>
  api.post('/editorial-board/reorder', { ordered_ids: orderedIds });

// ── Admin Users (superadmin) ──
export const getAdminUsers = (params) => api.get('/admin/users', { params });
export const assignJournalAdmin = (userId, journalId) =>
  api.post(`/admin/users/${userId}/journal-admin`, { journal_id: journalId });
export const removeJournalAdmin = (userId, journalId) =>
  api.delete(`/admin/users/${userId}/journal-admin/${journalId}`);
export const setUserRole = (userId, role) =>
  api.patch(`/admin/users/${userId}/role`, { role });
export const setUserActive = (userId, isActive) =>
  api.patch(`/admin/users/${userId}/active`, { is_active: isActive });

export default api;
